import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Brain, Star, Zap, Trophy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface FocusBoostProps {
  onClose: () => void;
}

const colors = [
  { id: "red", color: "bg-red-500", activeColor: "bg-red-300" },
  { id: "blue", color: "bg-blue-500", activeColor: "bg-blue-300" },
  { id: "green", color: "bg-green-500", activeColor: "bg-green-300" },
  { id: "yellow", color: "bg-yellow-500", activeColor: "bg-yellow-300" },
];

export const FocusBoost = ({ onClose }: FocusBoostProps) => {
  const [sequence, setSequence] = useState<string[]>([]);
  const [userSequence, setUserSequence] = useState<string[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUserTurn, setIsUserTurn] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [activeColor, setActiveColor] = useState<string | null>(null);
  const [gameOver, setGameOver] = useState(false);
  const [highScore, setHighScore] = useState(0);
  const { toast } = useToast();

  const playSequence = async (seq: string[]) => {
    setIsPlaying(true);
    setIsUserTurn(false);
    
    for (let i = 0; i < seq.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 500));
      setActiveColor(seq[i]);
      await new Promise(resolve => setTimeout(resolve, 600));
      setActiveColor(null);
    }
    
    setIsPlaying(false);
    setIsUserTurn(true);
  };

  const startGame = () => {
    const newSequence = [colors[Math.floor(Math.random() * colors.length)].id];
    setSequence(newSequence);
    setUserSequence([]);
    setCurrentLevel(1);
    setScore(0);
    setGameOver(false);
    playSequence(newSequence);
  };

  const handleColorClick = (colorId: string) => {
    if (!isUserTurn || isPlaying) return;

    const newUserSequence = [...userSequence, colorId];
    setUserSequence(newUserSequence);

    // Flash the color
    setActiveColor(colorId);
    setTimeout(() => setActiveColor(null), 300);

    // Check if the user's sequence matches so far
    if (colorId !== sequence[newUserSequence.length - 1]) {
      // Wrong color - game over
      setGameOver(true);
      setIsUserTurn(false);
      if (score > highScore) {
        setHighScore(score);
        toast({
          title: "New High Score!",
          description: `You reached level ${currentLevel} with ${score} points!`,
        });
      } else {
        toast({
          title: "Good Try!",
          description: `You reached level ${currentLevel}. Try again to beat your score!`,
          variant: "destructive",
        });
      }
      return;
    }

    // Check if user completed the sequence
    if (newUserSequence.length === sequence.length) {
      // Correct! Move to next level
      const newScore = score + (currentLevel * 10);
      setScore(newScore);
      setCurrentLevel(currentLevel + 1);
      setUserSequence([]);
      
      toast({
        title: "Great Memory!",
        description: `Level ${currentLevel} complete! +${currentLevel * 10} points`,
      });

      // Add a new color to the sequence
      setTimeout(() => {
        const newSequence = [...sequence, colors[Math.floor(Math.random() * colors.length)].id];
        setSequence(newSequence);
        playSequence(newSequence);
      }, 1000);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <Button
          variant="ghost"
          onClick={onClose}
          className="mb-6 gap-2"
        >
          <X className="h-4 w-4" />
          Exit Game
        </Button>

        <Card className="p-8 md:p-12">
          {!gameOver && sequence.length === 0 ? (
            // Welcome screen
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                <Brain className="h-12 w-12 text-white" />
              </div>
              <h2 className="text-3xl font-bold mb-4 text-foreground">Focus Boost Game</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Test your working memory by repeating the color sequence. Each level adds a new color!
              </p>
              
              {highScore > 0 && (
                <div className="bg-accent-soft/50 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center gap-2">
                    <Trophy className="h-5 w-5 text-accent" />
                    <span className="text-foreground font-semibold">High Score: {highScore}</span>
                  </div>
                </div>
              )}

              <Button size="lg" onClick={startGame} className="gap-2 shadow-medium">
                <Zap className="h-5 w-5" />
                Start Playing
              </Button>
            </div>
          ) : gameOver ? (
            // Game over screen
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                <Star className="h-12 w-12 text-white" />
              </div>
              <h2 className="text-3xl font-bold mb-4 text-foreground">Game Over!</h2>
              <p className="text-lg text-muted-foreground mb-2">
                You reached Level {currentLevel}
              </p>
              <p className="text-2xl font-bold text-primary mb-6">
                Final Score: {score}
              </p>
              
              {score === highScore && score > 0 && (
                <p className="text-accent font-semibold mb-6 flex items-center justify-center gap-2">
                  <Trophy className="h-5 w-5" />
                  New High Score!
                </p>
              )}

              <div className="flex gap-3 justify-center">
                <Button onClick={startGame} className="gap-2">
                  <Zap className="h-4 w-4" />
                  Play Again
                </Button>
                <Button variant="outline" onClick={onClose}>
                  Back to Toolkit
                </Button>
              </div>
            </div>
          ) : (
            // Game screen
            <>
              {/* Score & Level */}
              <div className="flex justify-between items-center mb-8">
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">Level</div>
                  <div className="text-3xl font-bold text-primary">{currentLevel}</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">Score</div>
                  <div className="text-3xl font-bold text-foreground">{score}</div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-1">Best</div>
                  <div className="text-3xl font-bold text-accent">{highScore}</div>
                </div>
              </div>

              {/* Status Message */}
              <div className="text-center mb-8">
                {isPlaying && (
                  <p className="text-lg font-semibold text-primary animate-pulse">
                    Watch the sequence...
                  </p>
                )}
                {isUserTurn && (
                  <p className="text-lg font-semibold text-accent">
                    Your turn! Repeat the pattern ({userSequence.length}/{sequence.length})
                  </p>
                )}
              </div>

              {/* Color Grid */}
              <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto mb-8">
                {colors.map((colorObj) => (
                  <button
                    key={colorObj.id}
                    onClick={() => handleColorClick(colorObj.id)}
                    disabled={!isUserTurn || isPlaying}
                    className={`
                      h-32 rounded-xl transition-all duration-200 
                      ${activeColor === colorObj.id ? colorObj.activeColor : colorObj.color}
                      ${isUserTurn && !isPlaying ? 'hover:scale-105 cursor-pointer shadow-lg' : 'opacity-70'}
                      disabled:cursor-not-allowed
                      ${activeColor === colorObj.id ? 'scale-110 shadow-2xl' : ''}
                    `}
                  />
                ))}
              </div>

              {/* Quit Button */}
              <div className="text-center">
                <Button variant="outline" onClick={onClose} size="sm">
                  Quit Game
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};
